// JWT 토큰 관리 유틸리티

let accessToken = null;

// Access Token 저장 (메모리 + localStorage)
export const setAccessToken = (token) => {
  accessToken = token;
  if (token) {
    localStorage.setItem('accessToken', token);
  } else {
    localStorage.removeItem('accessToken');
  }
};

// Access Token 가져오기 (메모리 우선, 없으면 localStorage에서)
export const getAccessToken = () => {
  if (accessToken) {
    return accessToken;
  }
  
  // 메모리에 없으면 localStorage에서 가져오기
  const storedToken = localStorage.getItem('accessToken');
  if (storedToken) {
    accessToken = storedToken;
    return storedToken;
  }
  
  return null;
};

// Access Token 제거
export const removeAccessToken = () => {
  accessToken = null;
  localStorage.removeItem('accessToken');
};

// JWT 토큰 파싱
export const parseJWT = (token) => {
  try {
    if (!token) return null;
    
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT format');
      return null;
    }
    
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    
    // Base64 디코딩
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('JWT 파싱 실패:', error);
    return null;
  }
};

// 토큰에서 사용자 정보 추출 (백엔드 클레임 구조에 맞춤)
export const getLoginInfoFromToken = (token) => {
  const payload = parseJWT(token);
  if (!payload) {
    console.error('JWT 페이로드를 추출할 수 없습니다.');
    return null;
  }
  
  return {
    userUid: payload.userUid,
    signInId: payload.signInId || payload.sub,
    name: payload.name,
    roles: payload.roles || [],
    exp: payload.exp,
    iat: payload.iat,
    iss: payload.iss,
  };
};

// 토큰 만료 확인
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  const payload = parseJWT(token);
  if (!payload || !payload.exp) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
};

// 토큰 갱신 필요 여부 확인 (만료 5분 전)
export const shouldRefreshToken = (token) => {
  if (!token) return true;
  
  const payload = parseJWT(token);
  if (!payload || !payload.exp) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  const refreshThreshold = 5 * 60; // 5분
  return (payload.exp - currentTime) < refreshThreshold;
};

// 토큰 만료까지 남은 시간 (초)
export const getTimeUntilExpiry = (token) => {
  if (!token) return 0;
  
  const payload = parseJWT(token);
  if (!payload || !payload.exp) return 0;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return Math.max(0, payload.exp - currentTime);
};

// 토큰 유효성 검증 (형식 + 만료시간)
export const isValidToken = (token) => {
  if (!token) return false;
  
  // JWT 형식 확인
  const payload = parseJWT(token);
  if (!payload) return false;
  
  // 만료시간 확인
  if (isTokenExpired(token)) return false;
  
  return true;
};

// Refresh Token 갱신 (API 호출)
export const refreshJWT = async () => {
  try {
    const apiHost = process.env.REACT_APP_API_HOST || 'http://localhost:8080';
    
    const response = await fetch(`${apiHost}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include', // 쿠키에 있는 refresh token 포함
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {
      const data = await response.json();
      if (data.accessToken) {
        setAccessToken(data.accessToken);
        return data.accessToken;
      } else {
        throw new Error('Access token not found in response');
      }
    } else {
      // 401이나 403 등의 에러 응답 처리
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: Token refresh failed`);
    }
  } catch (error) {
    // console.error('토큰 갱신 실패:', error);
    
    // 토큰 갱신 실패 시 로컬 토큰 제거
    removeAccessToken();
    
    // throw error;
  }
};

// 토큰 자동 갱신 (만료 5분 전에 갱신)
export const autoRefreshToken = async () => {
  const currentToken = getAccessToken();
  
  if (!currentToken) {
    // console.log('토큰이 없어 자동 갱신을 건너뜁니다.');
    return null;
  }
  
  if (shouldRefreshToken(currentToken)) {
    try {
      // console.log('토큰 자동 갱신을 시작합니다.');
      const newToken = await refreshJWT();
      // console.log('토큰 자동 갱신이 완료되었습니다.');
      return newToken;
    } catch (error) {
      // console.error('토큰 자동 갱신 실패:', error);
      return null;
    }
  }
  
  return currentToken;
};

// 디버깅용: 토큰 정보 출력
export const debugToken = (token = null) => {
  const targetToken = token || getAccessToken();
  
  if (!targetToken) {
    console.log('토큰이 없습니다.');
    return;
  }
  
  const payload = parseJWT(targetToken);
  const userInfo = getLoginInfoFromToken(targetToken);
  const expired = isTokenExpired(targetToken);
  const timeLeft = getTimeUntilExpiry(targetToken);
  
  console.group('🔍 JWT Token Debug Info');
  console.log('Token:', targetToken.substring(0, 50) + '...');
  console.log('Payload:', payload);
  console.log('User Info:', userInfo);
  console.log('Is Expired:', expired);
  console.log('Time Until Expiry:', timeLeft, 'seconds');
  console.log('Should Refresh:', shouldRefreshToken(targetToken));
  console.groupEnd();
};

// 초기화 함수 (앱 시작 시 호출)
export const initializeJWT = () => {
  // 필요한 경우 여기서 저장된 토큰 복구 로직 추가
  // 현재는 메모리 기반이므로 별도 처리 없음
  console.log('JWT 유틸리티가 초기화되었습니다.');
};

// 모든 JWT 관련 데이터 정리
export const clearAllJWTData = () => {
  removeAccessToken();
  console.log('모든 JWT 데이터가 정리되었습니다.');
};